import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { BayesianProbe, ProbeOption } from '../types/engagement';
type Tone = 'light' | 'dark';
interface BayesianOverlayProps {
  probe: BayesianProbe;
  tone?: Tone;
  onClose: () => void;
}
type Phase = 'prior' | 'evidence' | 'posterior';
const EASE = [0.23, 1, 0.32, 1] as const;
const THEME = {
  light: {
    surface: 'bg-paper',
    divider: 'border-line',
    eyebrow: 'text-ozBlue',
    heading: 'text-ozNavy',
    body: 'text-mut',
    optionIdle: 'border-line bg-paper hover:border-ozBlue',
    optionMine: 'border-ozGreen bg-ozGreen/[0.12]',
    optionOther: 'border-line bg-paper opacity-45',
    optionText: 'text-ozNavy',
    share: 'text-ozBlue',
    priorEcho: 'bg-ozMist text-mut',
    priorEchoStrong: 'text-ozNavy',
    stat: 'text-ozNavy',
    primary: 'bg-ozNavy text-paper',
    secondary: 'border border-line text-ozNavy hover:border-ozBlue',
    track: 'bg-line',
    close: 'text-mut hover:text-ozNavy',
    fine: 'text-mut'
  },
  dark: {
    surface: 'bg-ozNavy',
    divider: 'border-paper/12',
    eyebrow: 'text-ozGreen',
    heading: 'text-paper',
    body: 'text-paper/70',
    optionIdle: 'border-paper/20 bg-paper/[0.05] hover:border-ozGreen',
    optionMine: 'border-ozGreen bg-ozGreen/[0.18]',
    optionOther: 'border-paper/10 bg-transparent opacity-40',
    optionText: 'text-paper',
    share: 'text-ozGreen',
    priorEcho: 'bg-paper/[0.07] text-paper/60',
    priorEchoStrong: 'text-paper',
    stat: 'text-ozGreen',
    primary: 'bg-ozGreen text-ozNavy',
    secondary: 'border border-paper/25 text-paper hover:border-ozGreen',
    track: 'bg-paper/20',
    close: 'text-paper/60 hover:text-paper',
    fine: 'text-paper/45'
  }
} as const;
function OptionList({
  options,
  selected,
  onSelect,
  theme
}: {
  options: ProbeOption[];
  selected: string | null;
  onSelect: (id: string) => void;
  theme: (typeof THEME)[Tone];
}) {
  return <div className="mt-4 flex flex-col gap-2" data-id="element-30" data-mp="6io3b4mh5o6n">
      {options.map(option => {
      const isMine = selected === option.id;
      return <button key={option.id} type="button" onClick={() => !selected && onSelect(option.id)} aria-pressed={isMine} className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozGreen ${selected ? isMine ? `cursor-default ${theme.optionMine}` : `cursor-default ${theme.optionOther}` : theme.optionIdle}`} data-id="element-31" data-mp="1juon8vgbr4w">
            <span className={`text-[12.5px] font-medium leading-snug ${theme.optionText}`} data-id="element-32" data-mp="35z49ypors92g">
              {option.label}
            </span>
            {selected && option.share !== undefined && <span className={`shrink-0 text-[12px] font-bold tabular-nums ${theme.share}`} data-id="element-33" data-mp="2vzvji6t45cku">
                {option.share}%
              </span>}
          </button>;
    })}
    </div>;
}
export function BayesianOverlay({
  probe,
  tone = 'light',
  onClose
}: BayesianOverlayProps) {
  const [phase, setPhase] = useState<Phase>('prior');
  const [prior, setPrior] = useState<string | null>(null);
  const [posterior, setPosterior] = useState<string | null>(null);
  const theme = THEME[tone];
  const priorChoice = probe.priorOptions.find(o => o.id === prior);
  const posteriorChoice = probe.posteriorOptions.find(o => o.id === posterior);
  const choosePrior = (id: string) => {
    setPrior(id);
    window.setTimeout(() => setPhase('evidence'), 420);
  };
  const steps: Phase[] = ['prior', 'evidence', 'posterior'];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} role="dialog" aria-modal="true" aria-label={probe.trigger} className={`absolute inset-0 z-20 flex flex-col ${theme.surface}`} data-id="element-34" data-mp="a0lomwrr99pm">
      <div className={`flex items-center justify-between gap-3 border-b px-5 py-3 ${theme.divider}`} data-id="element-35" data-mp="3m44uxae4vso2">
        <div className="flex items-center gap-1.5" aria-hidden="true" data-id="element-36" data-mp="4nx0wid2y6e2">
          {steps.map(s => <span key={s} className={`h-1 w-6 rounded-full transition-colors duration-150 ${steps.indexOf(phase) >= steps.indexOf(s) ? 'bg-ozGreen' : theme.track}`} data-id="element-37" data-mp="3w0hv8r46p4rs" />)}
        </div>
        <button type="button" onClick={onClose} aria-label="Close question" className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozGreen ${theme.close}`} data-id="element-38" data-mp="nlg1cxlh2gov">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-39" data-mp="2kej8d4abz9vm" />
        </button>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto px-5 py-5" data-id="element-40" data-mp="zvywhgceoykj">
        <AnimatePresence mode="wait" data-id="element-41" data-mp="zy1ykyqu2i5m">
          {phase === 'prior' && <motion.div key="prior" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="flex flex-1 flex-col justify-center" data-id="element-42" data-mp="14tqrij8cl0sy">
              <p className={`text-[9.5px] uppercase tracking-micro ${theme.eyebrow}`} data-id="element-43" data-mp="h3wgm77l9mbc">
                {probe.priorLabel}
              </p>
              <h3 className={`mt-3 font-display text-[19px] font-semibold leading-snug ${theme.heading}`} data-id="element-44" data-mp="779xbmuwv3bf">
                {probe.priorQuestion}
              </h3>
              <OptionList options={probe.priorOptions} selected={prior} onSelect={choosePrior} theme={theme} data-id="element-45" data-mp="21lb2sqkyuokh" />
            </motion.div>}

          {phase === 'evidence' && <motion.div key="evidence" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="flex flex-1 flex-col" data-id="element-46" data-mp="254jo07gkmxpa">
              <p className={`text-[9.5px] uppercase tracking-micro ${theme.eyebrow}`} data-id="element-47" data-mp="1q7slvqfij7vz">
                {probe.evidenceLabel}
              </p>

              {priorChoice && <p className={`mt-2.5 rounded-lg px-3 py-2 text-[11px] leading-snug ${theme.priorEcho}`} data-id="element-48" data-mp="2ivojcxcwp3tt">
                  Your prior:{' '}
                  <span className={theme.priorEchoStrong} data-id="element-49" data-mp="295dd345r2ijr">{priorChoice.label}</span>
                </p>}

              <p className={`mt-5 font-display text-[30px] font-bold leading-none tracking-tight ${theme.stat}`} data-id="element-50" data-mp="36l99q1be33d4">
                {probe.evidenceStat}
              </p>
              <p className={`mt-3.5 text-[12.5px] leading-relaxed ${theme.body}`} data-id="element-51" data-mp="3a36pzf1tbyp8">
                {probe.evidenceText}
              </p>

              <button type="button" onClick={() => setPhase('posterior')} className={`mt-auto flex h-11 w-full items-center justify-center rounded-full text-[13px] font-semibold transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozGreen ${theme.primary}`} data-id="element-52" data-mp="3e5tg9biu5bkb">
                Update your view
              </button>
            </motion.div>}

          {phase === 'posterior' && <motion.div key="posterior" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="flex flex-1 flex-col" data-id="element-53" data-mp="5q9qwnlacdaj">
              <p className={`text-[9.5px] uppercase tracking-micro ${theme.eyebrow}`} data-id="element-54" data-mp="kitrphkyq8ot">
                {probe.posteriorLabel}
              </p>
              <h3 className={`mt-3 font-display text-[18px] font-semibold leading-snug ${theme.heading}`} data-id="element-55" data-mp="10w065k9plkjg">
                {probe.posteriorQuestion}
              </h3>

              <OptionList options={probe.posteriorOptions} selected={posterior} onSelect={setPosterior} theme={theme} data-id="element-56" data-mp="19yszdt2gc6bx" />

              {posteriorChoice && <motion.p initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.24,
            ease: EASE
          }} className={`mt-4 border-l-[3px] border-ozGreen pl-3.5 text-[12.5px] leading-relaxed ${theme.heading}`} data-id="element-57" data-mp="i4h3xfnieaih">
                  {probe.closing}
                </motion.p>}

              <p className={`mt-auto pt-4 text-[8.5px] leading-snug ${theme.fine}`} data-id="element-58" data-mp="3ulw3jxkywwi6">
                {probe.reference}
              </p>

              <button type="button" onClick={onClose} className={`mt-3 flex h-11 w-full items-center justify-center rounded-full text-[13px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozGreen ${theme.secondary}`} data-id="element-59" data-mp="2ex6c5fah3478">
                Back to the science
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}